import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AudienceSettingsTab from './AudienceSettingsTab';
import AccountSettingsTab from './AccountSettingsTab';
import { TutorialProvider, useTutorial } from 'contexts/TutorialContext';
import { useTranslation } from 'react-i18next';

// Define type for active tab
type TabType = 'audience-settings' | 'account-settings';

// Inner component that uses tutorial context
const TabsInner: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>(); // Extract accountId from URL
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('audience-settings');
  const { startTutorial } = useTutorial();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab') as TabType | null;
    if (tab && (tab === 'audience-settings' || tab === 'account-settings')) {
      setActiveTab(tab);
    } else {
      setActiveTab('audience-settings'); // Default to audience-settings
    }
  }, [location]);

  // Check if we should start the tutorial
  useEffect(() => {
    const state = location.state as { startTutorial?: boolean };
    if (state?.startTutorial) {
      // Clear the state and start tutorial after a short delay
      navigate(location.pathname + location.search, { replace: true, state: {} });
      setTimeout(() => {
        startTutorial();
      }, 500);
    }
  }, [location, navigate, startTutorial]);

  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`?tab=${tab}`);
  };

  const tabs = [
    { id: 'audience-settings' as TabType, label: t('accountTabs.tabs.audience') },
    { id: 'account-settings' as TabType, label: t('accountTabs.tabs.account') },
  ];

  return (
    <div className="min-h-screen">
      <div className="px-6 py-6">
        {/* Tabs Navigation */}
        <div className="flex gap-2 border-b border-gray-200 mb-5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`px-4 py-2 text-xs font-medium rounded-t-md transition-colors ${
                activeTab === tab.id
                  ? "bg-blue-50 text-[#007bff] border-l border-t border-r border-blue-100"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              onClick={() => handleTabClick(tab.id)}
              aria-label={`Switch to ${tab.label}`}
              aria-pressed={activeTab === tab.id}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Conditional Content Rendering */}
        <div>
          {activeTab === 'audience-settings' && <AudienceSettingsTab />}
          {activeTab === 'account-settings' && <AccountSettingsTab />}
        </div>
      </div>
    </div>
  );
};

// Wrapper component with provider
const Tabs: React.FC = () => {
  return (
    <TutorialProvider>
      <TabsInner />
    </TutorialProvider>
  );
};

export default Tabs;
